import React, { useState, useRef, useEffect } from 'react';
import Modal from '../../General/Modal';
import { useUrlDataContext } from '../lib/UrlDataContext';
import { updateUrlData } from '../../../api/url_data';
import cogoToast from 'cogo-toast';
import './DismissUrlData.scss';

export const DismissUrlData = ({ canUnmatch }) => {
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [escalationReason, setEscalationReason] = useState('');
  const { removeUrlData, currentUrlData, isCurrentUrlBlocked, isDirectVisitToCurrentUrl } = useUrlDataContext();
  const updatingRef = useRef(false);

  // The reservation can be lost while the modal is open; closing it keeps the actions from firing
  // against a row whose state just changed.
  useEffect(() => {
    if (isCurrentUrlBlocked) setShowModal(false);
  }, [isCurrentUrlBlocked]);

  const handleDismiss = action => async () => {
    if (updatingRef.current) return;
    updatingRef.current = true;

    try {
      setUpdating(true);

      if (!currentUrlData) {
        console.error('No URL data found');
        return;
      }

      const payload =
        action === 're-crawl'
          ? { recrawl: true, metadata: { humanCatalogAction: 'recrawl' } }
          : action === 'unmatch'
          ? { Product_id: null, metadata: { humanCatalogAction: 'unmatch' } }
          : { dismissedAt: Date.now(), metadata: { humanCatalogAction: 'dismiss_url' } };
      if (isDirectVisitToCurrentUrl) payload.overrideSecondOpinion = true;
      await updateUrlData(currentUrlData.hashedUrlStem, payload);

      cogoToast.success(action === 're-crawl' ? 'URL sent back through crawl' : action === 'unmatch' ? 'URL unmatched' : 'URL dismissed');
      setShowModal(false);
      removeUrlData(currentUrlData.hashedUrlStem, { release: true });
    } catch (error) {
      cogoToast.error(typeof error === 'string' ? error : 'There was an issue dismissing the URL');
    } finally {
      updatingRef.current = false;
      setUpdating(false);
    }
  };

  const handleEscalate = async e => {
    e.preventDefault();
    if (updatingRef.current) return;
    updatingRef.current = true;

    try {
      setUpdating(true);

      await updateUrlData(currentUrlData.hashedUrlStem, {
        escalationData: {
          reason: escalationReason
        },
        escalatedAt: Date.now(),
        metadata: { humanCatalogAction: 'escalate' },
        ...(isDirectVisitToCurrentUrl ? { overrideSecondOpinion: true } : {})
      });

      cogoToast.success('URL escalated');
      setEscalationReason('');
      setShowModal(false);
      removeUrlData(currentUrlData.hashedUrlStem);
    } catch (error) {
      cogoToast.error(typeof error === 'string' ? error : 'There was an issue escalating the URL');
    } finally {
      updatingRef.current = false;
      setUpdating(false);
    }
  };

  const handleTextareaKeyDown = e => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleEscalate(e);
    }
  };

  return (
    <>
      <div>
        <button className='dismiss-url-data-button' onClick={() => setShowModal(true)} disabled={updating || isCurrentUrlBlocked}>
          Dismiss
        </button>
      </div>

      {showModal && (
        <Modal close={() => setShowModal(false)}>
          <div className='dismiss-url-data-modal'>
            <header>
              <h1>Dismiss URL</h1>
              <p>How would you like to dismiss this URL?</p>
            </header>

            <div>
              <h3>Escalate</h3>
              <p>If you're unsure how to handle this URL, you can escalate it to the team to have someone else take a look.</p>

              <form onSubmit={handleEscalate}>
                <label>
                  Explain why you're escalating this URL:
                  <textarea required value={escalationReason} onChange={e => setEscalationReason(e.target.value)} onKeyDown={handleTextareaKeyDown} />
                </label>
                <button type='submit' disabled={updating || isCurrentUrlBlocked}>
                  Escalate
                </button>
              </form>
            </div>

            {canUnmatch && (
              <div>
                <h3>Unmatch</h3>
                <p>
                  If this page is matched to an incorrect Product and there isn't a reason to match it now, you can clear the match and it will be
                  re-prioritized for matching.
                </p>
                <button onClick={handleDismiss('unmatch')} disabled={updating || isCurrentUrlBlocked}>
                  Unmatch url and associated pins
                </button>
              </div>
            )}

            <div>
              <h3>Re-crawl</h3>
              <p>
                If the page looks like a product page which we should create a product for, you can send it back through the pipeline to try and fetch
                better data.
              </p>
              <button onClick={handleDismiss('re-crawl')} disabled={updating || isCurrentUrlBlocked}>
                Add back to queue
              </button>
            </div>

            <div>
              <h3>Permanently</h3>
              <p>If the url is not a product page, is low quality, or doesn't align with our products, you can dismiss it permanently.</p>
              <button onClick={handleDismiss('permanently')} disabled={updating || isCurrentUrlBlocked}>
                Dismiss permanently
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
